/**
 * Drip campaign hooks — campaigns + steps. Mirrors adcare-pl's
 * `api.marketing.*` tRPC surface but plain fetch with credentials.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { env } from './env';

export const DRIP_CAMPAIGNS_KEY = ['admin', 'drip', 'campaigns'] as const;
export const DRIP_CAMPAIGN_KEY = (id: string) =>
  ['admin', 'drip', 'campaigns', id] as const;

export type CampaignKind = 'booking' | 'recall' | 'newsletter';
export type Audience = 'booking_confirmed' | 'booking_completed' | 'manual';
export type CampaignStatus = 'draft' | 'active' | 'paused' | 'archived';
export type Channel = 'sms' | 'email';
export type Anchor =
  | 'booking_start'
  | 'booking_end'
  | 'campaign_start'
  | 'prev_step'
  | 'last_visit';

export interface DripCampaign {
  id: string;
  tenant_id: string;
  name: string;
  kind: CampaignKind;
  audience: Audience;
  status: CampaignStatus;
  starts_at: number | null;
  created_at: number;
  updated_at: number;
  step_count?: number;
}

export interface DripStep {
  id: string;
  campaign_id: string;
  position: number;
  channel: Channel;
  anchor: Anchor;
  offset_minutes: number;
  time_of_day: string | null;
  body: string;
  enabled: boolean;
  fires?: { total: number; delivered: number };
}

async function fx(path: string, init: RequestInit = {}) {
  return fetch(`${env.apiBaseUrl}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
}

export function useDripCampaigns() {
  return useQuery({
    queryKey: DRIP_CAMPAIGNS_KEY,
    queryFn: async (): Promise<DripCampaign[]> => {
      const r = await fx('/api/admin/drip');
      if (!r.ok) throw new Error('campaigns_failed');
      const j = (await r.json()) as { campaigns: DripCampaign[] };
      return j.campaigns;
    },
  });
}

export function useDripCampaign(id: string | undefined) {
  return useQuery({
    queryKey: id ? DRIP_CAMPAIGN_KEY(id) : ['admin', 'drip', 'campaigns', 'none'],
    enabled: !!id,
    queryFn: async (): Promise<{ campaign: DripCampaign; steps: DripStep[] }> => {
      const r = await fx(`/api/admin/drip/${id}`);
      if (!r.ok) throw new Error('campaign_failed');
      return (await r.json()) as { campaign: DripCampaign; steps: DripStep[] };
    },
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      kind: CampaignKind;
      audience: Audience;
      starts_at?: number;
    }): Promise<{ id: string }> => {
      const r = await fx('/api/admin/drip', { method: 'POST', body: JSON.stringify(input) });
      if (!r.ok) throw new Error('create_failed');
      return (await r.json()) as { id: string };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: DRIP_CAMPAIGNS_KEY }),
  });
}

export function usePatchCampaign(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<Pick<DripCampaign, 'name' | 'status'>> & { starts_at?: number | null }) => {
      const r = await fx(`/api/admin/drip/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
      if (!r.ok) throw new Error('patch_failed');
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DRIP_CAMPAIGN_KEY(id) });
      qc.invalidateQueries({ queryKey: DRIP_CAMPAIGNS_KEY });
    },
  });
}

export function useAddStep(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Omit<DripStep, 'id' | 'campaign_id' | 'fires'>) => {
      const r = await fx(`/api/admin/drip/${campaignId}/steps`, {
        method: 'POST',
        body: JSON.stringify(input),
      });
      if (!r.ok) throw new Error('add_step_failed');
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: DRIP_CAMPAIGN_KEY(campaignId) }),
  });
}

export function usePatchStep(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; patch: Partial<DripStep> }) => {
      const r = await fx(`/api/admin/drip/${campaignId}/steps/${input.id}`, {
        method: 'PATCH',
        body: JSON.stringify(input.patch),
      });
      if (!r.ok) throw new Error('patch_step_failed');
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: DRIP_CAMPAIGN_KEY(campaignId) }),
  });
}

export function useDeleteStep(campaignId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (stepId: string) => {
      const r = await fx(`/api/admin/drip/${campaignId}/steps/${stepId}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('delete_step_failed');
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: DRIP_CAMPAIGN_KEY(campaignId) }),
  });
}

export interface RecallAudience {
  months_ago: number;
  cutoff: number;
  due_now: number;
  due_this_week: number;
  total_patients: number;
}

/** Audience preview for recall campaigns — how many patients are due now/soon. */
export function useRecallAudience(monthsAgo: number, enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'drip', 'recall-audience', monthsAgo],
    enabled,
    queryFn: async (): Promise<RecallAudience> => {
      const r = await fx(`/api/admin/drip/recall-audience?months=${monthsAgo}`);
      if (!r.ok) throw new Error('audience_failed');
      return (await r.json()) as RecallAudience;
    },
    staleTime: 60_000,
  });
}
